import { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, Wrench, School, TrendingUp, Loader2 } from "lucide-react";
import { API, auth, guarded, label } from "./shared";

const cardCls = "border border-[#E6E2DC] bg-white p-6";

function Breakdown({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className={cardCls} data-testid={testid}>
      <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A837B]">{title}</h3>
      <div className="mt-5 flex flex-col gap-3">
        {rows.length === 0 && <p className="text-sm text-[#8A837B]">No data yet.</p>}
        {rows.map((r) => (
          <div key={String(r._id)}>
            <div className="flex justify-between text-xs">
              <span className="font-semibold capitalize text-[#141210]">{label(r._id) || "unknown"}</span>
              <span className="text-[#6B655F]">{r.count}</span>
            </div>
            <div className="mt-1 h-1.5 w-full bg-[#F1EDE6]">
              <div className="h-1.5 bg-[#581820] transition-[width] duration-700" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard({ token, onAuthError }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    guarded(async () => {
      const { data: d } = await axios.get(`${API}/dashboard`, auth(token));
      setData(d);
    }, onAuthError);
  }, [token, onAuthError]);

  if (!data) {
    return (
      <div className="flex items-center gap-3 py-20 text-[#8A837B]" data-testid="dashboard-loading">
        <Loader2 size={18} className="animate-spin" /> Loading dashboard…
      </div>
    );
  }

  const cards = [
    { label: "New Leads — 7 Days", value: data.enquiries_week, icon: TrendingUp, testid: "stat-new-leads" },
    { label: "Total Enquiries", value: data.enquiries_total, icon: Inbox, testid: "stat-total-enquiries" },
    { label: "Open Tickets", value: data.tickets_open, icon: Wrench, testid: "stat-open-tickets" },
    { label: "Schools", value: data.schools_total, icon: School, testid: "stat-schools" },
  ];

  return (
    <div data-testid="dashboard-tab">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className={cardCls} data-testid={c.testid}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A837B]">{c.label}</span>
              <c.icon size={16} className="text-[#581820]" />
            </div>
            <div className="mt-4 font-display text-4xl font-semibold text-[#141210]">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Breakdown title="Leads By Stage" rows={data.leads_by_stage} testid="breakdown-leads-stage" />
        <Breakdown title="Leads By Topic" rows={data.leads_by_topic} testid="breakdown-leads-topic" />
        <Breakdown title="Tickets By Status" rows={data.tickets_by_status} testid="breakdown-tickets-status" />
        <Breakdown title="Tickets By Priority" rows={data.tickets_by_priority} testid="breakdown-tickets-priority" />
        <Breakdown title="Schools By Stage" rows={data.schools_by_stage} testid="breakdown-schools-stage" />
      </div>
    </div>
  );
}
